/**
 * Midnight & Lavender chart theme.
 *
 * One source of truth for every chart in the app: the palettes, the ECharts
 * theme object built from them, and the light/dark mode detection that keeps
 * both in sync with the `dark` class ThemeContext puts on <html>.
 *
 * The palettes are CVD-validated per mode — each mode's slots were checked for
 * the OKLCH lightness band, the chroma floor, adjacent-pair separation under
 * simulated protanopia/deuteranopia (OKLab ΔE), the normal-vision floor, and
 * contrast against that mode's card surface. They are pinned in
 * `charts.theme.test.tsx`: changing a hex means re-validating the whole set,
 * not eyeballing a nicer purple.
 *
 * Colour rules the call sites follow:
 *   - categorical slots carry series *identity*, assigned in fixed order and
 *     never cycled (past the last slot, fold into "Others" grey);
 *   - the sequential ramp is purple-only, light -> dark, for magnitude;
 *   - status colours (good / warning / critical) are reserved for verdicts —
 *     matched vs unmatched, safe vs risky — never for "series 4";
 *   - sentiment and matched/unmatched are the exceptions: they read as series
 *     identity next to the rest of the board, so they wear the teal/pink
 *     categorical slots (`sentimentSliceColor`, `matchOutcomeColors`) rather
 *     than status green/red.
 */

export type ChartMode = 'light' | 'dark';

/** Series identity. Fixed order, assigned in sequence, never cycled. */
export const CATEGORICAL_PALETTE: Record<ChartMode, string[]> = {
    light: ['#8D5DD9', '#0E9B8E', '#2F7FE8', '#E15FA6', '#4B3FA8', '#0D8FBF'],
    dark: ['#A175E5', '#14A896', '#4A8FEE', '#D8639F', '#7868D6', '#1FA0CF'],
};

/**
 * IAB top-level category colours (deepcontext-ec6z.3). The classifier's
 * top-level ids run 102–127 (url-extractor `IAB_CATEGORIES`); each gets its
 * own hue, evenly spaced around the wheel. Consecutive ids step by a stride
 * coprime to the slot count so neighbouring ids never land on neighbouring
 * hues. An id outside that range still gets a stable golden-angle hue.
 */
const IAB_FIRST_ID = 102;
const IAB_SLOT_COUNT = 26;
const IAB_HUE_STRIDE = 7;
const IAB_HUE_OFFSET = 215;
const GOLDEN_ANGLE_DEG = 137.508;
const IAB_SATURATION: Record<ChartMode, number> = { light: 0.62, dark: 0.7 };
const IAB_LIGHTNESS: Record<ChartMode, number> = { light: 0.54, dark: 0.62 };

function hslToHex(h: number, s: number, l: number): string {
    const a = s * Math.min(l, 1 - l);
    const channel = (n: number) => {
        const k = (n + h / 30) % 12;
        const v = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
        return Math.round(v * 255)
            .toString(16)
            .padStart(2, '0');
    };
    return `#${channel(0)}${channel(8)}${channel(4)}`;
}

/** One stable colour per IAB category id, per mode; `null` (unclassified) is the neutral grey. */
export function iabCategoryColor(iabId: number | null, mode: ChartMode): string {
    if (iabId == null) return STATUS_PALETTE[mode].neutral;
    const slot = iabId - IAB_FIRST_ID;
    const hue =
        slot >= 0 && slot < IAB_SLOT_COUNT
            ? IAB_HUE_OFFSET + ((slot * IAB_HUE_STRIDE) % IAB_SLOT_COUNT) * (360 / IAB_SLOT_COUNT)
            : iabId * GOLDEN_ANGLE_DEG;
    return hslToHex(((hue % 360) + 360) % 360, IAB_SATURATION[mode], IAB_LIGHTNESS[mode]);
}

/** Up/down slots inside the categorical palette: teal up, pink down. */
const POSITIVE_CATEGORICAL_SLOT = 1;
const NEGATIVE_CATEGORICAL_SLOT = 3;

/**
 * Magnitude. One hue (brand purple), light -> dark, monotone lightness.
 *
 * Part of the theme contract, not wired-up behaviour: none of today's five
 * charts encode a continuous magnitude, so nothing consumes this ramp yet. It
 * is defined (and pinned in the tests) so the first heatmap/choropleth/tier
 * ramp reaches for these validated steps instead of inventing purples.
 */
export const SEQUENTIAL_PALETTE: Record<ChartMode, string[]> = {
    light: ['#C29BFF', '#A87BEB', '#8D5DD9', '#7645BF', '#5E3399', '#472573'],
    dark: ['#EFE3FF', '#DDC8FF', '#C29BFF', '#A87BEB', '#8D5DD9', '#7645BF'],
};

export interface StatusColors {
    good: string;
    warning: string;
    critical: string;
    neutral: string;
}

/** Verdicts only. Deliberately outside the categorical slots. */
export const STATUS_PALETTE: Record<ChartMode, StatusColors> = {
    light: { good: '#0E8A4C', warning: '#A06B00', critical: '#D43D3D', neutral: '#8B7FA6' },
    dark: { good: '#75DAAA', warning: '#FFD548', critical: '#FF8A8A', neutral: '#8F93A0' },
};

export interface ChartChrome {
    /** The card the chart sits on — also the colour of the 2px mark gaps/rings. */
    surface: string;
    ink: string;
    inkMuted: string;
    gridline: string;
    accent: string;
    tooltipBg: string;
    tooltipInk: string;
    tooltipBorder: string;
}

export const CHROME: Record<ChartMode, ChartChrome> = {
    light: {
        surface: '#FFFFFF',
        ink: '#2C2140',
        inkMuted: '#8B7FA6',
        gridline: '#ECE3FA',
        accent: '#8D5DD9',
        tooltipBg: '#FFFFFF',
        tooltipInk: '#2C2140',
        tooltipBorder: '#ECE3FA',
    },
    dark: {
        surface: '#26282E',
        ink: '#F0EDF8',
        inkMuted: '#8F93A0',
        gridline: '#33363D',
        accent: '#C29BFF',
        tooltipBg: '#1F2125',
        tooltipInk: '#F0EDF8',
        tooltipBorder: '#33363D',
    },
};

export const CHART_FONT_FAMILY = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";

export interface ChartPalette {
    mode: ChartMode;
    categorical: string[];
    sequential: string[];
    status: StatusColors;
    chrome: ChartChrome;
}

function domMode(root?: HTMLElement): ChartMode {
    const el = root ?? (typeof document === 'undefined' ? undefined : document.documentElement);
    return el?.classList.contains('dark') ? 'dark' : 'light';
}

/** The mode the DOM is currently painted in (ThemeContext owns the class). */
export function currentChartMode(): ChartMode {
    return domMode();
}

// CSS custom properties only ever hold the mode the DOM is painted in, so a
// palette read for the *other* mode has to come from the constants above.
function cssVar(name: string, fallback: string, root?: HTMLElement): string {
    if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') return fallback;
    const el = root ?? document.documentElement;
    const value = getComputedStyle(el).getPropertyValue(name).trim();
    return value || fallback;
}

/**
 * Resolve a mode's palette, preferring the CSS variables in index.css so a
 * token change repaints the charts too. Falls back to the validated constants
 * (which is also what happens under jsdom, where the stylesheet is stubbed).
 */
export function readChartPalette(mode: ChartMode, root?: HTMLElement): ChartPalette {
    const live = mode === domMode(root);
    const read = (name: string, fallback: string) => (live ? cssVar(name, fallback, root) : fallback);

    return {
        mode,
        categorical: CATEGORICAL_PALETTE[mode].map((hex, i) => read(`--chart-${i + 1}`, hex)),
        sequential: SEQUENTIAL_PALETTE[mode].map((hex, i) => read(`--chart-seq-${i + 1}`, hex)),
        status: {
            good: read('--chart-good', STATUS_PALETTE[mode].good),
            warning: read('--chart-warning', STATUS_PALETTE[mode].warning),
            critical: read('--chart-critical', STATUS_PALETTE[mode].critical),
            neutral: read('--chart-neutral', STATUS_PALETTE[mode].neutral),
        },
        chrome: {
            surface: read('--surface', CHROME[mode].surface),
            ink: read('--ink', CHROME[mode].ink),
            inkMuted: read('--ink-muted', CHROME[mode].inkMuted),
            gridline: read('--line', CHROME[mode].gridline),
            accent: read('--accent', CHROME[mode].accent),
            tooltipBg: read('--chart-tooltip-bg', CHROME[mode].tooltipBg),
            tooltipInk: read('--chart-tooltip-ink', CHROME[mode].tooltipInk),
            tooltipBorder: read('--chart-tooltip-border', CHROME[mode].tooltipBorder),
        },
    };
}

/**
 * The hue a sentiment slice wears, in either mode. Positive and Negative take
 * fixed categorical slots (teal / pink) so the two sentiment donuts on the
 * media-planning board match; everything else (Neutral, unknown) is grey.
 */
export function sentimentSliceColor(label: string, palette: ChartPalette): string {
    if (label === 'Positive') return palette.categorical[POSITIVE_CATEGORICAL_SLOT];
    if (label === 'Negative') return palette.categorical[NEGATIVE_CATEGORICAL_SLOT];
    return palette.status.neutral;
}

/**
 * The teal/pink pair the Analysis Overview board paints Matched vs Unmatched
 * with — donut slices, their tinted legend rows and the trend bars all read
 * from here so the three surfaces can never drift apart.
 */
export function matchOutcomeColors(palette: ChartPalette): { matched: string; unmatched: string } {
    return {
        matched: palette.categorical[POSITIVE_CATEGORICAL_SLOT],
        unmatched: palette.categorical[NEGATIVE_CATEGORICAL_SLOT],
    };
}

interface AxisTheme {
    axisLine: { show: boolean };
    axisTick: { show: boolean };
    axisLabel: { color: string; fontSize: number; fontFamily: string };
    splitLine: { show: boolean; lineStyle: { color: string; width: number; type: string } };
}

export interface ChartTheme {
    color: string[];
    backgroundColor: string;
    textStyle: Record<string, unknown>;
    categoryAxis: AxisTheme;
    valueAxis: AxisTheme;
    legend: Record<string, unknown>;
    tooltip: Record<string, unknown>;
    line: Record<string, unknown>;
    bar: Record<string, unknown>;
    pie: Record<string, unknown>;
}

/** Build the ECharts theme object for a mode from that mode's palette. */
export function buildChartTheme(mode: ChartMode, root?: HTMLElement): ChartTheme {
    const { categorical, chrome } = readChartPalette(mode, root);

    // No axis lines and no ticks anywhere: the hairline gridline carries the
    // scale and the labels sit directly under/next to the marks.
    const axis = (splitLine: boolean): AxisTheme => ({
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: chrome.inkMuted, fontSize: 11, fontFamily: CHART_FONT_FAMILY },
        splitLine: {
            show: splitLine,
            lineStyle: { color: chrome.gridline, width: 1, type: 'solid' },
        },
    });

    return {
        color: categorical,
        backgroundColor: 'transparent',
        textStyle: { fontFamily: CHART_FONT_FAMILY, color: chrome.ink },
        categoryAxis: axis(false),
        valueAxis: axis(true),
        legend: {
            icon: 'roundRect',
            itemWidth: 10,
            itemHeight: 10,
            itemGap: 18,
            textStyle: { color: chrome.inkMuted, fontSize: 12, fontFamily: CHART_FONT_FAMILY },
        },
        tooltip: {
            backgroundColor: chrome.tooltipBg,
            borderColor: chrome.tooltipBorder,
            borderWidth: 1,
            borderRadius: 12,
            padding: [10, 12],
            textStyle: { color: chrome.tooltipInk, fontSize: 12, fontFamily: CHART_FONT_FAMILY },
            extraCssText: 'box-shadow: 0 8px 24px rgba(44, 33, 64, 0.14);',
            axisPointer: {
                type: 'line',
                lineStyle: { color: chrome.gridline, width: 1, type: 'solid' },
            },
        },
        // Smooth 2.5px lines with round caps; markers big enough to hover.
        line: {
            smooth: true,
            symbol: 'circle',
            symbolSize: 8,
            lineStyle: { width: 2.5, cap: 'round', join: 'round' },
            itemStyle: { borderWidth: 2, borderColor: chrome.surface },
        },
        bar: { itemStyle: { borderColor: chrome.surface, borderWidth: 0 } },
        // Rounded, padded donut slices: the 2px surface border is the gap.
        pie: { itemStyle: { borderColor: chrome.surface, borderWidth: 2, borderRadius: 6 } },
    };
}

export function chartThemeName(mode: ChartMode): string {
    return `deepcontext-${mode}`;
}

interface ThemeRegistrar {
    registerTheme: (name: string, theme: object) => void;
}

/**
 * (Re-)register a mode's theme with ECharts. Called on every mount and on
 * every theme toggle, so a token change picked up from CSS lands on the next
 * chart instance rather than being frozen at first import.
 */
export function registerChartTheme(echarts: ThemeRegistrar, mode: ChartMode, root?: HTMLElement): string {
    const name = chartThemeName(mode);
    echarts.registerTheme(name, buildChartTheme(mode, root));
    return name;
}
